'use client';
import { FormikHelpers, FormikErrors } from 'formik';
import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import './SignupStep3Form.style.scss';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import Text from '@/components/common/Text/Text';
import Select from '../lib/InputField/Select';
import AddressInputField from '../lib/InputField/AddressInputField';
import { US_STATES } from '@/util/user_client';
import { isValidCityName } from '@/util/regexp';

export type SignupStep3FormValues = {
  address: string;
  aptUnitSuite?: string;
  city: string;
  state: string;
  postalCode: string;
};

interface SignupStep3FormProps
  extends Omit<BaseFormProps<SignupStep3FormValues>, 'children' | 'onSubmit'> {
  onSubmit?: (
    values: SignupStep3FormValues,
    actions: FormikHelpers<SignupStep3FormValues>
  ) => void;
}

function defaultValidation(
  values: SignupStep3FormValues
): FormikErrors<SignupStep3FormValues> {
  const { address, city, state, postalCode } = values;
  const errors: FormikErrors<SignupStep3FormValues> = {};

  if (!address) {
    errors.address = 'Please enter a valid address';
  }
  if (!city || !isValidCityName(city)) {
    errors.city = 'Please enter a valid city';
  }
  if (!state) {
    errors.state = 'Please enter a valid state';
  }

  if (!postalCode || postalCode.length !== 5) {
    errors.postalCode = 'Please enter a valid zip code';
  }

  return errors;
}

export default function SignupStep3Form(props: SignupStep3FormProps) {
  const {
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    validateOnBlur = true,
    validateOnChange = true
  } = props;

  const handleSubmit = async (values: SignupStep3FormValues) => {
    // Your form submission logic goes in this function.
    // If your form submission fails, call this method:

    onSubmitFailure?.(values /* OptionalDataResponseHere */);

    // If the submission is successful, call this method:
    onSubmitSuccess?.(values /* OptionalDataResponseHere */);
  };

  function parseAddress(address: string): SignupStep3FormValues {
    // Sometimes address can be undefined if a user doesn't trigger the auto complete
    // (hit enter without selecting a suggested address)
    // If that's the case, return object where the props are empty strings to trigger formik validation
    if (!address) {
      return {
        address: '',
        city: '',
        state: '',
        postalCode: ''
      } as SignupStep3FormValues;
    }

    const addressArray = address.split(',');
    const fullAddress: SignupStep3FormValues = {
      address: addressArray[0] || '',
      city: addressArray[1]?.trim() || '',
      state: addressArray[2]?.trim().split(' ')[0] || '',
      postalCode: addressArray[2]?.trim().split(' ')[1] || ''
    };

    return fullAddress;
  }

  return (
    <div className='SignupStep3Form'>
      <h1 className='Signup-step-title'>Complete your application</h1>
      <div className='Signup-progress-bar'>
        <div
          className='Progress-bar-fill'
          style={{ width: '60%' }}
        ></div>
      </div>
      <h3 className='Signup-step-subtitle'>Shipping</h3>

      <Form<SignupStep3FormValues>
        initialValues={initialValues}
        validate={validate}
        // If an 'onSubmit function' is passed in, that function will override
        // this component's 'handleSubmit' function.
        onSubmit={onSubmit || handleSubmit}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
      >
        <AddressInputField
          className='Signup-address'
          label={'Address*'}
          onAddressSelected={(formikContext, formattedAddress) => {
            const { city, state, postalCode } = parseAddress(formattedAddress);

            formikContext.setFieldValue('city', city, false);
            formikContext.setFieldValue('state', state, false);
            formikContext.setFieldValue('postalCode', postalCode, false);
          }}
          name='address'
          defaultValue={initialValues.address}
        />
        <div className='Signup-fields-wrapper'>
          <InputField
            className='Signup-apt-unit-suite'
            name='aptUnitSuite'
            label={'apt/unit/suite (optional)'}
            type='text'
          />
          <InputField
            className='Signup-city'
            name='city'
            label={'City'}
            type='text'
          />
          <Select
            name='state'
            label={'State'}
            options={US_STATES}
          />
          <MaskedField
            id='postalCode'
            className='Signup-postal-code'
            guide={false}
            mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
            label={'Postal Code'}
            name='postalCode'
          />
        </div>

        <Text
          className='Signup-footnote'
          variant='text-s'
        >
          *Please note PO Boxes are not accepted
        </Text>

        <button
          className='Signup-Continue-Button cursor'
          type='submit'
        >
          Continue
        </button>
      </Form>
    </div>
  );
}
